import { type ReactNode, Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import type { Role } from '../lib/types';
import { hasRole, homeFor, useMe } from '../lib/session';
import { Layout } from '../components/Layout';
import { ErrorMessage, Loading, PageTitle } from '../components/ui';
import { LoginPage, TwoFactorPage } from '../pages/auth/LoginPage';
import { InvitationPage } from '../pages/auth/InvitationPage';
import { ConfirmEmailPage, RegisterPage, ResetPasswordPage } from '../pages/auth/PasswordPages';
import { ConsentPage } from '../pages/account/ConsentPage';
import { AccountPage } from '../pages/account/AccountPage';
import { BookingPage } from '../pages/patient/BookingPage';
import { AppointmentsPage } from '../pages/patient/AppointmentsPage';
import { PaymentReturnPage, VisitPage } from '../pages/public/VisitPages';

// Large, role-specific parts are loaded on demand (FullCalendar, SignalR, admin screens).
const MessagesPage = lazy(() => import('../pages/chat/MessagesPage').then((m) => ({ default: m.MessagesPage })));
const CalendarPage = lazy(() => import('../pages/staff/CalendarPage').then((m) => ({ default: m.CalendarPage })));
const AvailabilityPage = lazy(() => import('../pages/staff/AvailabilityPage').then((m) => ({ default: m.AvailabilityPage })));
const PatientsPage = lazy(() => import('../pages/staff/PatientsPage').then((m) => ({ default: m.PatientsPage })));
const PatientDetailsPage = lazy(() => import('../pages/staff/PatientsPage').then((m) => ({ default: m.PatientDetailsPage })));
const InquiriesPage = lazy(() => import('../pages/inquiries/InquiriesPage').then((m) => ({ default: m.InquiriesPage })));
const ProfilePage = lazy(() => import('../pages/staff/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const TherapistsAdminPage = lazy(() => import('../pages/admin/AdminPages').then((m) => ({ default: m.TherapistsAdminPage })));
const ServicesPage = lazy(() => import('../pages/admin/AdminPages').then((m) => ({ default: m.ServicesPage })));
const RoomsPage = lazy(() => import('../pages/admin/AdminPages').then((m) => ({ default: m.RoomsPage })));
const ReportsPage = lazy(() => import('../pages/admin/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const ImportPage = lazy(() => import('../pages/admin/ImportPage').then((m) => ({ default: m.ImportPage })));
const SettingsPage = lazy(() => import('../pages/admin/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const EmbedPage = lazy(() => import('../pages/admin/EmbedPage').then((m) => ({ default: m.EmbedPage })));
const MessageTemplatesPage = lazy(() => import('../pages/admin/MessageTemplatesPage').then((m) => ({ default: m.MessageTemplatesPage })));
const AuditLogPage = lazy(() => import('../pages/admin/AdminPages').then((m) => ({ default: m.AuditLogPage })));
const NotificationLogPage = lazy(() => import('../pages/admin/NotificationLogPage').then((m) => ({ default: m.NotificationLogPage })));

/**
 * Requires a session. Users who still have to accept consents or (staff) set up MFA are sent there first;
 * role restrictions only shape the UI – the API enforces them independently.
 */
function RequireUser({ roles, children }: { roles?: Role[]; children: ReactNode }) {
  const { data: me, isPending, error } = useMe();
  const location = useLocation();
  if (isPending) return <Loading />;
  if (error) return <ErrorMessage error={error} />;
  if (!me) return <Navigate to={`/logowanie?returnUrl=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (me.consentRequired && location.pathname !== '/zgody') return <Navigate to="/zgody" replace />;
  if (!me.consentRequired && me.mfaSetupRequired && location.pathname !== '/konto') return <Navigate to="/konto" replace />;
  if (roles && !hasRole(me, ...roles)) return <Navigate to={homeFor(me)} replace />;
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

function Home() {
  const { data: me, isPending } = useMe();
  if (isPending) return <Loading />;
  return <Navigate to={me ? homeFor(me) : '/logowanie'} replace />;
}

function NotFound() {
  return <PageTitle>Nie znaleziono strony</PageTitle>;
}

export function App() {
  const patient: Role[] = ['Patient'];
  const therapist: Role[] = ['Therapist'];
  const staff: Role[] = ['Admin', 'Reception', 'Therapist'];
  const manager: Role[] = ['Admin', 'Reception'];
  const admin: Role[] = ['Admin'];

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="logowanie" element={<LoginPage />} />
        <Route path="logowanie/2fa" element={<TwoFactorPage />} />
        <Route path="zaproszenie" element={<InvitationPage />} />
        <Route path="reset-hasla" element={<ResetPasswordPage />} />
        <Route path="rejestracja" element={<RegisterPage />} />
        <Route path="potwierdz-email" element={<ConfirmEmailPage />} />
        <Route path="konto/potwierdz-email" element={<ConfirmEmailPage change />} />
        <Route path="wizyta" element={<VisitPage />} />
        <Route path="platnosc" element={<PaymentReturnPage />} />

        <Route path="zgody" element={<RequireUser><ConsentPage /></RequireUser>} />
        <Route path="konto" element={<RequireUser><AccountPage /></RequireUser>} />
        <Route path="konto/metody-logowania" element={<RequireUser><AccountPage /></RequireUser>} />

        <Route path="rezerwacja" element={<RequireUser roles={patient}><BookingPage /></RequireUser>} />
        <Route path="wizyty" element={<RequireUser roles={patient}><AppointmentsPage /></RequireUser>} />
        <Route path="wiadomosci" element={<RequireUser roles={['Patient', 'Therapist']}><MessagesPage /></RequireUser>} />
        <Route path="wiadomosci/:conversationId" element={<RequireUser roles={['Patient', 'Therapist']}><MessagesPage /></RequireUser>} />

        <Route path="kalendarz" element={<RequireUser roles={therapist}><CalendarPage /></RequireUser>} />
        <Route path="dostepnosc" element={<RequireUser roles={therapist}><AvailabilityPage /></RequireUser>} />
        <Route path="pacjenci" element={<RequireUser roles={staff}><PatientsPage /></RequireUser>} />
        <Route path="pacjenci/:patientId" element={<RequireUser roles={staff}><PatientDetailsPage /></RequireUser>} />
        <Route path="pytania" element={<RequireUser roles={staff}><InquiriesPage /></RequireUser>} />
        <Route path="pytania/:inquiryId" element={<RequireUser roles={staff}><InquiriesPage /></RequireUser>} />
        <Route path="profil" element={<RequireUser roles={therapist}><ProfilePage /></RequireUser>} />
        <Route path="raport" element={<RequireUser roles={therapist}><ReportsPage own /></RequireUser>} />

        <Route path="placowka/kalendarz" element={<RequireUser roles={manager}><CalendarPage clinicWide /></RequireUser>} />
        <Route path="placowka/pacjenci" element={<RequireUser roles={manager}><PatientsPage /></RequireUser>} />
        <Route path="placowka/pacjenci/:patientId" element={<RequireUser roles={manager}><PatientDetailsPage /></RequireUser>} />
        <Route path="placowka/terapeuci" element={<RequireUser roles={manager}><TherapistsAdminPage /></RequireUser>} />
        <Route path="placowka/terapeuci/:therapistId/dostepnosc" element={<RequireUser roles={manager}><AvailabilityPage /></RequireUser>} />
        <Route path="placowka/raporty" element={<RequireUser roles={manager}><ReportsPage /></RequireUser>} />
        <Route path="placowka/uslugi" element={<RequireUser roles={admin}><ServicesPage /></RequireUser>} />
        <Route path="placowka/gabinety" element={<RequireUser roles={admin}><RoomsPage /></RequireUser>} />
        <Route path="placowka/audyt" element={<RequireUser roles={admin}><AuditLogPage /></RequireUser>} />
        <Route path="placowka/powiadomienia" element={<RequireUser roles={admin}><NotificationLogPage /></RequireUser>} />
        <Route path="placowka/import" element={<RequireUser roles={admin}><ImportPage /></RequireUser>} />
        <Route path="placowka/ustawienia" element={<RequireUser roles={admin}><SettingsPage /></RequireUser>} />
        <Route path="placowka/wiadomosci" element={<RequireUser roles={admin}><MessageTemplatesPage /></RequireUser>} />
        <Route path="placowka/osadzanie" element={<RequireUser roles={admin}><EmbedPage /></RequireUser>} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
